/**
 * services/auditService.ts
 *
 * Trading performance audit service for MER Algo Footprints v5.1
 *
 * Observational layer — records setups and computes performance metrics.
 * Does NOT place trades. Does NOT modify engine logic.
 *
 * Usage:
 *   import { createAuditRecord, computeAuditMetrics } from './auditService';
 */

import {
  AuditRecord, AuditMetrics,
  TrendAlignmentBucket, ConfidenceBucket,
  TradePlan, WaitRecord,
  DrawdownInfo, StreakInfo, ExecutionQualityStats,
  WaitAccuracyStats, BehavioralStats, EquityCurvePoint,
} from '../../../types';
import { TradeSetup } from '../../../types';
import { SymbolContext } from '../../../services/buildSymbolContext';
import { RiskAssessment } from '../../../risk/riskEngine';
import { ValidatedAIResponse } from '../../../ai/responseSchema';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function confidenceBucket(c: number): ConfidenceBucket {
  if (c < 40) return '0-39';
  if (c < 55) return '40-54';
  if (c < 70) return '55-69';
  if (c < 85) return '70-84';
  return '85+';
}

function trendAlignmentBucket(
  dir: 'long' | 'short',
  trendDir: string,
  trendState: string,
): TrendAlignmentBucket {
  if (trendState === 'compression' || trendState === 'transition' || trendDir === 'neutral') {
    return 'neutral_trend_context';
  }
  const bullDir = dir === 'long' && trendDir === 'bullish';
  const bearDir = dir === 'short' && trendDir === 'bearish';
  return (bullDir || bearDir) ? 'aligned_with_trend' : 'counter_trend';
}

function winRate(records: AuditRecord[]): number | null {
  const closed = records.filter(r => r.outcome === 'win' || r.outcome === 'loss' || r.outcome === 'breakeven');
  if (!closed.length) return null;
  return closed.filter(r => r.outcome === 'win').length / closed.length;
}

function avgR(records: AuditRecord[]): number | null {
  const withR = records.filter(r => r.realizedR !== null);
  if (!withR.length) return null;
  return withR.reduce((s, r) => s + r.realizedR!, 0) / withR.length;
}

function medianR(records: AuditRecord[]): number | null {
  const rs = records.filter(r => r.realizedR !== null).map(r => r.realizedR!).sort((a, b) => a - b);
  if (!rs.length) return null;
  const mid = Math.floor(rs.length / 2);
  return rs.length % 2 !== 0 ? rs[mid] : (rs[mid - 1] + rs[mid]) / 2;
}

function expectancy(records: AuditRecord[]): number | null {
  const wr = winRate(records);
  const ar = avgR(records);
  if (wr === null || ar === null) return null;
  const wins  = records.filter(r => r.outcome === 'win').map(r => r.realizedR ?? 0);
  const losses = records.filter(r => r.outcome === 'loss').map(r => r.realizedR ?? 0);
  if (!wins.length || !losses.length) return ar; // fallback to avgR
  const avgWin  = wins.reduce((s, v) => s + v, 0) / wins.length;
  const avgLoss = losses.reduce((s, v) => s + v, 0) / losses.length;
  return wr * avgWin + (1 - wr) * avgLoss;
}

function isContinuationSetup(setupType: string): boolean {
  return setupType.toLowerCase().includes('continuation');
}

function isReversalSetup(setupType: string): boolean {
  const s = setupType.toLowerCase();
  return s.includes('reversal') || s.includes('reversion') ||
         s.includes('fade') || s.includes('rejection');
}

function groupStats(
  records: AuditRecord[],
  key: keyof AuditRecord,
): Record<string, { count: number; winRate: number | null; avgR: number | null }> {
  const groups: Record<string, AuditRecord[]> = {};
  records.forEach(r => {
    const k = String(r[key] ?? 'unknown');
    if (!groups[k]) groups[k] = [];
    groups[k].push(r);
  });
  const result: Record<string, { count: number; winRate: number | null; avgR: number | null }> = {};
  Object.entries(groups).forEach(([k, recs]) => {
    result[k] = { count: recs.length, winRate: winRate(recs), avgR: avgR(recs) };
  });
  return result;
}

// ─── Create audit record from setup + context ──────────────────────────────

export function createAuditRecord(
  setup:    TradeSetup,
  ctx:      SymbolContext,
  risk:     RiskAssessment,
): AuditRecord {
  type RegimeWithPhase = typeof ctx.regime & { phase?: string };
  const phase = (ctx.regime as RegimeWithPhase | null)?.phase ?? 'RANGE';
  const trendDir   = ctx.trend?.trendDirection ?? 'neutral';
  const trendState = ctx.trend?.trendState     ?? 'transition';
  const trendStr   = ctx.trend?.trendStrength  ?? 0;
  const dir        = setup.dir as 'long' | 'short';

  return {
    id:                   `${ctx.symbol}_${Date.now()}_${Math.random().toString(36).slice(2,7)}`,
    createdAt:            Date.now(),
    symbol:               ctx.symbol,
    interval:             ctx.interval,
    setupType:            setup.type,
    direction:            dir,
    regime:               ctx.regime?.dominant ?? 'unknown',
    phase,
    trendDirection:       trendDir,
    trendState,
    trendStrength:        trendStr,
    smcBias:              ctx.smc?.bias ?? 'neutral',
    displacementDirection: ctx.smc?.displacementDirection ?? 'none',
    scenarioType:         ctx.smc?.scenarioType ?? 'none',
    confidence:           setup.confidence,
    readiness:            risk.readiness,
    entryPrice:           setup.entryPrice   ?? null,
    stopPrice:            setup.stopPrice    ?? null,
    target1Price:         setup.target1Price ?? null,
    target2Price:         setup.target2Price ?? null,
    status:               'open',
    outcome:              'open',
    exitPrice:            null,
    exitReason:           '',
    realizedR:            null,
    mfe:                  null,
    mae:                  null,
    trendAlignment:       trendAlignmentBucket(dir, trendDir, trendState),
    confidenceBucket:     confidenceBucket(setup.confidence),
    notes:                '',
    tags:                 [],
  };
}

// ─── Compute metrics from audit record array ────────────────────────────────

export function computeAuditMetrics(records: AuditRecord[]): AuditMetrics {
  const executed = records.filter(r => r.status !== 'skipped');
  const closed   = records.filter(r => r.status === 'closed');

  // Per confidence bucket
  const confBuckets: ConfidenceBucket[] = ['0-39', '40-54', '55-69', '70-84', '85+'];
  const byConfidenceBucket = {} as AuditMetrics['byConfidenceBucket'];
  confBuckets.forEach(b => {
    const recs  = closed.filter(r => r.confidenceBucket === b);
    const wr    = winRate(recs);
    const ar    = avgR(recs);
    // Calibration: higher bucket should produce better outcomes
    const bucketIdx  = confBuckets.indexOf(b);
    const prevBucket = bucketIdx > 0 ? confBuckets[bucketIdx - 1] : null;
    const prevAR     = prevBucket
      ? avgR(closed.filter(r => r.confidenceBucket === prevBucket))
      : null;
    const calibration = ar === null ? 'insufficient data'
      : prevAR === null ? 'baseline'
      : ar > prevAR ? '✓ higher than previous tier'
      : ar < prevAR ? '✗ lower than previous tier (miscalibrated)'
      : '≈ similar to previous tier';
    byConfidenceBucket[b] = { count: recs.length, winRate: wr, avgR: ar, calibration };
  });

  // Trend alignment buckets
  const taBuckets: TrendAlignmentBucket[] = ['aligned_with_trend','counter_trend','neutral_trend_context'];
  const byTrendAlignment = {} as AuditMetrics['byTrendAlignment'];
  taBuckets.forEach(b => {
    const recs = closed.filter(r => r.trendAlignment === b);
    byTrendAlignment[b] = { count: recs.length, winRate: winRate(recs), avgR: avgR(recs) };
  });

  // Continuation vs reversal — using canonical helpers
  const contRecs = closed.filter(r => isContinuationSetup(r.setupType));
  const revRecs  = closed.filter(r => isReversalSetup(r.setupType));

  // RVOL context (using trendStrength as proxy for market activity level)
  const lowRvol    = closed.filter(r => r.trendStrength < 0.3);
  const normalRvol = closed.filter(r => r.trendStrength >= 0.3);

  // Best/worst setup families by expectancy
  const bySetupType = groupStats(closed, 'setupType');
  const setupFamilies = Object.entries(bySetupType)
    .map(([t, s]) => {
      const recs = closed.filter(r => r.setupType === t);
      return { setupType: t, expectancy: expectancy(recs) ?? 0, count: s.count };
    })
    .filter(f => f.count >= 2)
    .sort((a, b) => b.expectancy - a.expectancy);

  // Confidence calibration: does higher confidence produce meaningfully better expectancy?
  const bucketExpectancies = confBuckets
    .map(b => expectancy(closed.filter(r => r.confidenceBucket === b)))
    .filter(e => e !== null) as number[];
  const confidenceCalibrated = bucketExpectancies.length >= 3 &&
    bucketExpectancies[bucketExpectancies.length - 1] > bucketExpectancies[0];

  return {
    total:       records.length,
    executed:    executed.length,
    closed:      closed.length,
    winRate:     winRate(closed),
    avgR:        avgR(closed),
    medianR:     medianR(closed),
    expectancy:  expectancy(closed),
    avgMFE:      closed.filter(r => r.mfe !== null).reduce((s,r) => s + r.mfe!, 0) / (closed.filter(r=>r.mfe!==null).length||1),
    avgMAE:      closed.filter(r => r.mae !== null).reduce((s,r) => s + r.mae!, 0) / (closed.filter(r=>r.mae!==null).length||1),
    bySetupType,
    byDirection:      groupStats(closed, 'direction'),
    byPhase:          groupStats(closed, 'phase'),
    byTrendAlignment,
    byConfidenceBucket,
    byReadiness:      groupStats(closed, 'readiness'),
    continuationVsReversal: {
      continuation: {
        total:      contRecs.length,
        winRate:    winRate(contRecs),
        avgR:       avgR(contRecs),
        medianR:    medianR(contRecs),
        expectancy: expectancy(contRecs),
      },
      reversal: {
        total:      revRecs.length,
        winRate:    winRate(revRecs),
        avgR:       avgR(revRecs),
        medianR:    medianR(revRecs),
        expectancy: expectancy(revRecs),
      },
    },
    rvolContext: {
      low:    { count: lowRvol.length,    avgR: avgR(lowRvol)    },
      normal: { count: normalRvol.length, avgR: avgR(normalRvol) },
    },
    bestSetupFamilies:  setupFamilies.slice(0, 5),
    worstSetupFamilies: [...setupFamilies].sort((a, b) => a.expectancy - b.expectancy).slice(0, 5),
    confidenceCalibrated,
  };
}

// ─── Export helpers ────────────────────────────────────────────────────────

export function exportAuditToJSON(records: AuditRecord[]): string {
  return JSON.stringify(records, null, 2);
}

export function exportAuditToCSV(records: AuditRecord[]): string {
  if (!records.length) return '';
  const headers = Object.keys(records[0]) as (keyof AuditRecord)[];
  const rows = records.map(r =>
    headers.map(h => {
      const v = r[h];
      if (Array.isArray(v)) return `"${v.join(';')}"`;
      if (typeof v === 'string' && v.includes(',')) return `"${v}"`;
      return String(v ?? '');
    }).join(',')
  );
  return [headers.join(','), ...rows].join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// v5 ADDITIVE EXTENSIONS
// All existing functions above are untouched.
// These additions provide TradePlan creation, WaitRecord creation, and
// extended analytics without modifying any existing behavior.
// ─────────────────────────────────────────────────────────────────────────────

// ─── TradePlan helpers ────────────────────────────────────────────────────────

function inferRiskTier(
  conviction: number,
  readiness: string,
  phase: string,
): TradePlan['riskTierSuggested'] {
  if (readiness === 'interesting' || conviction < 35) return 'avoid';
  if (phase === 'RANGE' && conviction < 50) return 'reduced';
  if (conviction >= 70 && (readiness === 'urgent' || readiness === 'ready')) return 'standard';
  return 'light';
}

export function createTradePlan(
  setup:      TradeSetup,
  ctx:        SymbolContext,
  risk:       RiskAssessment,
  aiResponse: ValidatedAIResponse | null,
): TradePlan {
  const trendDir   = ctx.trend?.trendDirection ?? 'neutral';
  const trendState = ctx.trend?.trendState     ?? 'transition';
  const trendStr   = ctx.trend?.trendStrength  ?? 0;
  const phase      = (ctx.regime as any)?.phase ?? 'RANGE';

  const conviction   = aiResponse?.conviction   ?? 0;
  const actionability = aiResponse?.actionability ?? 0;

  return {
    id:              `plan_${ctx.symbol}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    createdAt:       Date.now(),
    symbol:          ctx.symbol,
    interval:        ctx.interval,
    setupType:       setup.type,
    direction:       setup.dir as 'long' | 'short',
    bias:            aiResponse?.overallBias ?? 'neutral',
    conviction,
    actionability,
    regime:          ctx.regime?.dominant ?? 'unknown',
    phase,
    trendDirection:  trendDir,
    trendState,
    trendStrength:   trendStr,
    smcBias:         ctx.smc?.bias ?? 'neutral',
    scenarioType:    ctx.smc?.scenarioType ?? 'none',
    entryPlanned:    setup.entry,
    confirmationRule: setup.confirmation,
    stopPlanned:     setup.stopPrice   ?? null,
    target1Planned:  setup.target1Price ?? null,
    target2Planned:  setup.target2Price ?? null,
    managementPlan:  aiResponse?.targetLogic ?? `Monitor ${ctx.symbol} against ${setup.invalidation}`,
    abortRule:       setup.invalidation,
    riskTierSuggested: inferRiskTier(conviction, risk.readiness, phase),
    status:          'planned',
    linkedAuditId:   null,
    notes:           '',
    tags:            [],
  };
}

// ─── WaitRecord helpers ───────────────────────────────────────────────────────

export function createWaitRecord(
  symbol:           string,
  reason:           string,
  setupTypeAvoided: string,
  directionAvoided: 'long' | 'short' | 'both',
  ctx:              SymbolContext | null,
): WaitRecord {
  const phase  = (ctx?.regime as any)?.phase  ?? 'RANGE';
  const regime = ctx?.regime?.dominant ?? 'unknown';
  const price  = ctx?.curPrice ?? null;
  return {
    id:               `wait_${symbol}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    symbol,
    timestamp:        Date.now(),
    reason,
    regimeAtTime:     regime,
    phaseAtTime:      phase,
    setupTypeAvoided,
    directionAvoided,
    classification:   'pending',
    priceAtTime:      price,
    notes:            '',
  };
}

// ─── Extended metrics (purely additive — wraps existing closed records) ───────

export function computeDrawdown(records: AuditRecord[]): DrawdownInfo {
  const closed = records
    .filter(r => r.status === 'closed' && r.realizedR !== null)
    .sort((a, b) => a.createdAt - b.createdAt);
  if (!closed.length) return { maxDrawdown: null, currentDrawdown: null, peakEquityR: null, currentEquityR: null };

  let peak = 0, trough = 0, maxDD = 0, equity = 0;
  for (const r of closed) {
    equity += r.realizedR!;
    if (equity > peak) { peak = equity; trough = equity; }
    if (equity < trough) { trough = equity; }
    const dd = peak - trough;
    if (dd > maxDD) maxDD = dd;
  }
  return {
    maxDrawdown:     maxDD > 0 ? -maxDD : 0,
    currentDrawdown: peak > equity ? -(peak - equity) : 0,
    peakEquityR:     peak,
    currentEquityR:  equity,
  };
}

export function computeStreak(records: AuditRecord[]): StreakInfo {
  const closed = records
    .filter(r => r.status === 'closed' && (r.outcome === 'win' || r.outcome === 'loss'))
    .sort((a, b) => a.createdAt - b.createdAt);

  let cur = 0, maxW = 0, maxL = 0, streak = 0;
  // Cumulative win count per setup type — honest: "most wins by count", not longest consecutive streak
  const setupWins: Record<string, number> = {};

  for (const r of closed) {
    const isWin = r.outcome === 'win';
    if (isWin) {
      streak = streak > 0 ? streak + 1 : 1;
      maxW = Math.max(maxW, streak);
      setupWins[r.setupType] = (setupWins[r.setupType] ?? 0) + 1;
    } else {
      streak = streak < 0 ? streak - 1 : -1;
      maxL = Math.max(maxL, Math.abs(streak));
    }
  }
  cur = streak;
  // bestWinningSetup: setup type with most cumulative wins (honest: not longest consecutive streak)
  const bestWinningSetup = Object.entries(setupWins)
    .sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';

  return { currentStreak: cur, maxWinStreak: maxW, maxLossStreak: maxL, bestWinningSetup };
}

export function computeExecutionQuality(records: AuditRecord[]): ExecutionQualityStats {
  const closed = records.filter(r => r.status === 'closed' && r.realizedR !== null);

  const mfeSamples = closed.filter(r => r.mfe !== null && r.mfe > 0 && r.realizedR !== null);
  const avgMFECaptured = mfeSamples.length
    ? mfeSamples.reduce((s, r) => s + Math.min(r.realizedR! / r.mfe!, 1), 0) / mfeSamples.length
    : null;

  // avgMAE: average max adverse excursion in R — directly from the mae field (honest label)
  const maeSamples = closed.filter(r => r.mae !== null);
  const avgMAE = maeSamples.length
    ? maeSamples.reduce((s, r) => s + Math.abs(r.mae!), 0) / maeSamples.length
    : null;

  return {
    avgSlippage:       null,   // requires manual entry vs planned — not yet tracked
    avgExitEfficiency: null,   // requires T1/T2 capture at exit — not yet tracked
    avgMFECaptured,
    avgMAE,
  };
}

export function computeWaitAccuracy(waitRecords: WaitRecord[]): WaitAccuracyStats {
  const correctAvoidances   = waitRecords.filter(r => r.classification === 'correct_avoidance').length;
  const missedOpportunities = waitRecords.filter(r => r.classification === 'missed_opportunity').length;
  const pending             = waitRecords.filter(r => r.classification === 'pending').length;
  // neutral counts in total but does NOT enter the avoidanceRate denominator
  const denominator = correctAvoidances + missedOpportunities;
  return {
    total: waitRecords.length,
    correctAvoidances,
    missedOpportunities,
    pending,
    avoidanceRate: denominator > 0 ? correctAvoidances / denominator : null,
  };
}

export function computeBehavioral(records: AuditRecord[]): BehavioralStats {
  const closed = records.filter(r => r.status === 'closed' && r.realizedR !== null);

  // ── Timezone-safe Eastern Time hour extraction ────────────────────────────
  // Uses Intl.DateTimeFormat with America/New_York to determine the correct
  // market-session hour regardless of the machine's local timezone.
  // Falls back to UTC-5 offset (EST) if Intl is unavailable, which is a safe
  // approximation for U.S. equity market context (accepts ±1h DST imprecision).
  function getEasternHour(ts: number): number {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        hour: 'numeric',
        hour12: false,
      }).formatToParts(new Date(ts));
      const h = parts.find(p => p.type === 'hour')?.value;
      return h !== undefined ? parseInt(h, 10) : Math.floor(((ts / 3600000) % 24 + 19) % 24);
    } catch {
      // Fallback: subtract 5h for EST (UTC-5); accepts ±1h DST imprecision
      return Math.floor(((ts / 3600000) % 24 + 19) % 24);
    }
  }

  const byBucket: Record<string, AuditRecord[]> = {};
  closed.forEach(r => {
    const h = getEasternHour(r.createdAt);
    const label = bucketHour(h);
    if (!byBucket[label]) byBucket[label] = [];
    byBucket[label].push(r);
  });

  let bestR = -Infinity, worstR = Infinity;
  let bestTOD: string | null = null, worstTOD: string | null = null;
  for (const [label, recs] of Object.entries(byBucket)) {
    const ar = recs.reduce((s, r) => s + r.realizedR!, 0) / recs.length;
    if (ar > bestR)  { bestR  = ar; bestTOD  = label; }
    if (ar < worstR) { worstR = ar; worstTOD = label; }
  }
  return {
    avgHoldBars:    null,
    overtradeScore: null,
    bestTimeOfDay:  bestTOD,
    worstTimeOfDay: worstTOD,
  };
}

function bucketHour(h: number): string {
  if (h < 10) return 'Opening Range (9:30–10)';
  if (h < 11) return 'First Hour (10–11)';
  if (h < 14) return 'Midday (11–14)';
  if (h < 16) return 'Power Hour (14–16)';
  return 'Other';
}

export function buildEquityCurve(records: AuditRecord[]): EquityCurvePoint[] {
  const closed = records
    .filter(r => r.status === 'closed' && r.realizedR !== null)
    .sort((a, b) => a.createdAt - b.createdAt);
  let cum = 0;
  return closed.map((r, i) => {
    cum += r.realizedR!;
    return {
      timestamp:    r.createdAt,
      cumulativeR:  +cum.toFixed(2),
      tradeIndex:   i + 1,
      setupType:    r.setupType,
      outcome:      r.outcome,
    };
  });
}

// ─── TradePlan/WaitRecord export helpers ─────────────────────────────────────

export function exportTradePlansToCSV(plans: TradePlan[]): string {
  if (!plans.length) return '';
  const headers: (keyof TradePlan)[] = [
    'createdAt','symbol','setupType','direction','bias','conviction','regime','phase',
    'entryPlanned','stopPlanned','target1Planned','riskTierSuggested','status','linkedAuditId','notes',
  ];
  const rows = plans.map(p =>
    headers.map(h => {
      const v = p[h];
      if (typeof v === 'string' && v.includes(',')) return `"${v}"`;
      return String(v ?? '');
    }).join(',')
  );
  return [headers.join(','), ...rows].join('\n');
}

export function exportWaitRecordsToCSV(records: WaitRecord[]): string {
  if (!records.length) return '';
  const headers: (keyof WaitRecord)[] = [
    'timestamp','symbol','reason','setupTypeAvoided','directionAvoided',
    'regimeAtTime','phaseAtTime','classification','priceAtTime','notes',
  ];
  const rows = records.map(r =>
    headers.map(h => {
      const v = r[h];
      if (typeof v === 'string' && v.includes(',')) return `"${v}"`;
      return String(v ?? '');
    }).join(',')
  );
  return [headers.join(','), ...rows].join('\n');
}

// ─── Quant Intelligence wrappers (v5.3 additive) ──────────────────────────────
// Thin re-exports so consumers import from a single audit service surface.
// No existing functions modified.

export { computeConfidenceCalibration } from '../../../services/quant/confidenceCalibration';
export { computeExpectancyMatrix }      from '../../../services/quant/expectancyMatrix';
export { computeDecisionQuality, computeDecisionQualitySummary } from '../../../services/quant/decisionQuality';

// ─── Adaptive Edge System wrappers (v5.5 additive) ────────────────────────────
export { computeAdaptiveConfidence }    from '../../../services/adaptive/adaptiveConfidence';
export { computeAdaptiveSetupWeights }  from '../../../services/adaptive/adaptiveSetupWeights';
export { computeExposureIntelligence }  from '../../../services/adaptive/exposureIntelligence';

export { computeAdaptiveScore, computeAdaptiveScoreSummary } from '../../../services/adaptive/adaptiveScoring';
